import React, { useEffect, useState } from "react";
import {
  Button,
  Collapse,
  Divider,
  Form,
  Input,
  Select,
  Spin,
  Switch,
  Table,
  Typography,
  notification,
} from "antd";
import { get, post, put } from "../../../api/axios";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import ImagesUploadComponent from "../../control-sections/helper/ImagesUploadComponent"; // Your Google Map component
import { checkPermission } from "../../control-sections/auth/ProtectedRoutes";

const { Title } = Typography;
const { Panel } = Collapse;

const EditListings = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // STATES
  const [fromLoading, setFromLoading] = useState(false);
  const [inputsValue, setInputsValue] = useState({});
  const [imageList, setImageList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sellerOptions, setSellerOptions] = useState([]);
  const [propertyTypeOptions, setPropertyTypeOptions] = useState([]);
  const [listingTypeOptions, setListingTypeOptions] = useState([]);

  // HANDLERS
  const handleImageUpload = (imageUrl) => {
    console.log("Image URL:", imageUrl);
    setImageList((prev) => [...prev, imageUrl]);
  };

  const columnsForHistory = [
    {
      title: "Status",
      dataIndex: "status",
    },
    {
      title: "Notes",
      dataIndex: "notes",
    },
    {
      title: "Reviewer Name",
      dataIndex: "reviewer_name",
      render: (_, record, __) =>
        checkPermission("update-admin") ? (
          record.reviewer_id ? (
            <Link to={`/admins/edit/${record.reviewer_id}`}>
              {record.reviewer_name ? record.reviewer_name : record.reviewer_id}
            </Link>
          ) : (
            ""
          )
        ) : record.reviewer_name ? (
          record.reviewer_name
        ) : (
          record.reviewer_id
        ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
    },
  ];

  // SUBMITTERS
  const onFinish = (values) => {
    setLoading(true);
    if (!imageList.length) {
      notification.error({
        message: "Missing required images",
        description: "Please upload at least one image.",
      });
      setLoading(false);
      return;
    }

    let data = {
      ...inputsValue,
      ...values,
      images: imageList.map((image) => image.path || image.url),
    };

    data.is_furnished = data.is_furnished ? 1 : 0;
    data.is_active = data.is_active ? 1 : 0;
    data.is_approved = data.is_approved ? 1 : 0;

    delete data.changes;
    delete data.deleted_at;
    delete data.updated_at;
    delete data.created_at;

    put(`listings/${id}`, data)
      .then(() => {
        notification.success({ message: "Modified successfully" });
        setLoading(false);
        navigate("/listings");
      })
      .catch((err) => {
        notification.error({
          message: "Error!",
          description: err.response.data.message,
        });
        setLoading(false);
      });
  };

  useEffect(() => {
    const getSellers = () => {
      get("sellers").then((res) => {
        setSellerOptions(
          res.data.data.map((seller) => ({
            value: seller.id,
            label: seller.name,
          }))
        );
      });
    };

    const getTypes = () => {
      get("get-types").then((res) => {
        setPropertyTypeOptions(
          res?.data?.property_types
            ? res?.data?.property_types.map((propertyType) => ({
                label: propertyType.value,
                value : propertyType.key,
              }))
            : []
        );
        setListingTypeOptions(
          res?.data?.listing_types
            ? res?.data?.listing_types.map((listingType) => ({
                label: listingType.value,
                value : listingType.key,
              }))
            : []
        );
      });
    };

    getSellers();
    getTypes();
  }, []);

  useEffect(() => {
    get(`listings/${id}`).then((res) => {
      setInputsValue(res.data);
      let filteredImages = res.data.images.filter((image) => image != "");
      const result = filteredImages.map((p) => ({
        path: p.split("com/")[1],
        url: p,
      }));
      setImageList(result);
      setFromLoading(true);
    });
  }, []);

  return (
    <div>
      <Title level={2} className="text-main mb-2">
        <ArrowLeftOutlined onClick={() => navigate("/listings")} />
        Edit Listing
      </Title>

      {fromLoading ? (
        <div className="row">
          <Collapse defaultActiveKey={["1"]}>
            <Panel header="Listing Info" key="1">
              <Form
                className="wrapper-content"
                onFinish={onFinish}
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 15 }}
                initialValues={{
                  title: inputsValue.title,
                  price: inputsValue.price,
                  area: inputsValue.area,
                  location: inputsValue.location,
                  floor: inputsValue.floor,
                  rooms: inputsValue.rooms,
                  bathrooms: inputsValue.bathrooms,
                  description: inputsValue.description,
                  youtube_link: inputsValue.youtube_link,
                  rega_ad_license_number: inputsValue.rega_ad_license_number,
                  seller_id: inputsValue.seller_id,
                  property_type: inputsValue.property_type,
                  listing_type: inputsValue.listing_type,
                  is_furnished: inputsValue.is_furnished,
                  is_active: inputsValue.is_active,
                  is_approved: inputsValue.is_approved,
                }}
                autoComplete="off"
              >
                <div className="row">
                  <div className="row">
                    {/* Item */}
                    <div className="col-lg-6">
                      <Form.Item
                        label="Title"
                        name="title"
                        rules={[
                          {
                            required: true,
                            message: "This field is required!",
                          },
                        ]}
                      >
                        <Input placeholder="Type..." />
                      </Form.Item>
                    </div>
                    {/* Item */}
                    <div className="col-lg-6">
                      <Form.Item
                        label="Price"
                        name="price"
                        rules={[
                          {
                            required: true,
                            message: "This field is required!",
                          },
                        ]}
                      >
                        <Input type="number" placeholder="Type..." />
                      </Form.Item>
                    </div>
                    {/* Item */}
                    <div className="col-lg-6">
                      <Form.Item
                        label="Area"
                        name="area"
                        rules={[
                          {
                            required: true,
                            message: "This field is required!",
                          },
                        ]}
                      >
                        <Input type="number" placeholder="Type..." />
                      </Form.Item>
                    </div>
                    {/* Item */}
                    <div className="col-lg-6">
                      <Form.Item
                        label="Location"
                        name="location"
                        rules={[
                          {
                            required: true,
                            message: "This field is required!",
                          },
                        ]}
                      >
                        <Input placeholder="Type..." />
                      </Form.Item>
                    </div>
                    {/* Item */}
                    <div className="col-lg-6">
                      <Form.Item
                        label="Floor"
                        name="floor"
                        rules={[
                          {
                            required: true,
                            message: "This field is required!",
                          },
                        ]}
                      >
                        <Input type="number" placeholder="Type..." />
                      </Form.Item>
                    </div>
                    {/* Item */}
                    <div className="col-lg-6">
                      <Form.Item
                        label="Rooms"
                        name="rooms"
                        rules={[
                          {
                            required: true,
                            message: "This field is required!",
                          },
                        ]}
                      >
                        <Input type="number" placeholder="Type..." />
                      </Form.Item>
                    </div>
                    {/* Item */}
                    <div className="col-lg-6">
                      <Form.Item
                        label="Bathrooms"
                        name="bathrooms"
                        rules={[
                          {
                            required: true,
                            message: "This field is required!",
                          },
                        ]}
                      >
                        <Input type="number" placeholder="Type..." />
                      </Form.Item>
                    </div>
                    {/* Item */}
                    <div className="col-lg-6">
                      <Form.Item
                        label="Description"
                        name="description"
                        rules={[
                          {
                            required: true,
                            message: "This field is required!",
                          },
                        ]}
                      >
                        <Input placeholder="Type..." />
                      </Form.Item>
                    </div>
                    {/* Item */}
                    <div className="col-lg-6">
                      <Form.Item
                        label="Youtube Link"
                        name="youtube_link"
                        rules={[
                          {
                            pattern:
                              /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/,
                            message: "Please enter a valid YouTube link!",
                          },
                        ]}
                      >
                        <Input placeholder="Type..." />
                      </Form.Item>
                    </div>
                    {/* Item */}
                    <div className="col-lg-6">
                      <Form.Item
                        label="License Number"
                        name="rega_ad_license_number"
                        rules={[
                          {
                            required: true,
                            message: "This field is required!",
                          },
                          {
                            pattern: /^\d{10}$/,
                            message:
                              "License number must be exactly 10 digits!",
                          },
                        ]}
                      >
                        <Input type="number" placeholder="Type..." />
                      </Form.Item>
                    </div>
                    {/* Item */}
                    <div className="col-lg-6">
                      <Form.Item
                        label="Seller"
                        name="seller_id"
                        rules={[
                          {
                            required: true,
                            message: "This field is required!",
                          },
                        ]}
                      >
                        <Select
                          className="w-100"
                          placeholder="select..."
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            (option?.label ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          options={sellerOptions}
                        />
                      </Form.Item>
                    </div>
                    {/* Item */}
                    <div className="col-lg-6">
                      <Form.Item
                        label="Property Type"
                        name="property_type"
                        rules={[
                          {
                            required: true,
                            message: "This field is required!",
                          },
                        ]}
                      >
                        <Select
                          className="w-100"
                          placeholder="select..."
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            (option?.label ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          onChange={() => {}}
                          options={propertyTypeOptions}
                        />
                      </Form.Item>
                    </div>
                    {/* Item */}
                    <div className="col-lg-6">
                      <Form.Item
                        label="Listing Type"
                        name="listing_type"
                        rules={[
                          {
                            required: true,
                            message: "This field is required!",
                          },
                        ]}
                      >
                        <Select
                          className="w-100"
                          placeholder="select..."
                          optionFilterProp="children"
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          onChange={() => {}}
                          options={listingTypeOptions}
                        />
                      </Form.Item>
                    </div>
                    {/* Item */}
                    <div className="col-lg-6">
                      <Form.Item label="Furnished" name="is_furnished">
                        <Switch defaultChecked={false} />
                      </Form.Item>
                    </div>
                    {/* Item */}
                    <div className="col-lg-6">
                      <Form.Item label="Active" name="is_active">
                        <Switch defaultChecked={false} />
                      </Form.Item>
                    </div>
                    {/* Item */}{" "}
                    {/* according to mahmoud request to remove approved field from edit form */}
                    {/* <div className="col-lg-6">
                <Form.Item label="Approved" name="is_approved">
                  <Switch defaultChecked={false} />
                </Form.Item>
              </div> */}
                    {/* Item */}
                    <div className="col-lg-12">
                      <ImagesUploadComponent
                        onImageUpload={handleImageUpload}
                        imageList={imageList}
                        setImageList={setImageList}
                        storagePath={"listings"}
                      />
                    </div>
                  </div>
                </div>
                <div className="row mt-5 pt-2">
                  <div className="col-lg-4 offset-lg-4">
                    <Form.Item
                      wrapperCol={{
                        offset: 1,
                        span: 16,
                      }}
                    >
                      <Button
                        loading={loading}
                        size="large"
                        block
                        type="primary"
                        htmlType="submit"
                      >
                        {loading ? "Updating..." : "Update"}
                      </Button>
                    </Form.Item>
                  </div>
                </div>
              </Form>
            </Panel>
            <Divider />
            <Panel header="History" key="2">
              <div className="row">
                <div className="w-100">
                  <Table
                    locale={{ emptyText: "There are no data" }}
                    loading={loading}
                    columns={columnsForHistory}
                    dataSource={inputsValue?.changes ?? []}
                    size="small"
                    responsive
                    pagination={false}
                  />
                </div>
              </div>
            </Panel>
          </Collapse>
        </div>
      ) : (
        <div className="text-center">
          <Spin size="large" />
        </div>
      )}
    </div>
  );
};

export default EditListings;
